/* ==========================================================================
   THROTTLE CO — global.js
   ==========================================================================

   Vanilla JS. No dependencies. Custom elements + document-level delegation.

   COMPONENTS / HOOKS (phase-2 builders: follow these)
   --------------------------------------------------------------------------
   <tc-details-disclosure>        wraps <details><summary> — syncs aria-expanded,
                                  closes on ESC / focus leaving.
   <tc-mega-menu data-hover>      header dropdown; extends disclosure with
                                  hover-intent open/close + outside-click close.
   <tc-drawer id="...">           off-canvas panel. Open with any
                                  [data-drawer-open="<id>"]; close with
                                  [data-drawer-close] inside, ESC, or backdrop.
                                  Also via window.tcDrawers.open/close(id).
   <tc-sticky-header data-sticky> adds .is-stuck when scrolled.
   <tc-announcement-bar data-rotate-interval="5000"> rotates
                                  [data-announcement-slide] children.
   <tc-carousel>                  scroll-snap track [data-carousel-track] +
                                  [data-carousel-prev]/[data-carousel-next] buttons.
   <tc-quantity>                  [data-qty-minus] / input / [data-qty-plus];
                                  fires 'change' on the input.
   <tc-product-form>              wraps a {% form 'product' %} — AJAX add to cart.
   <tc-predictive-search>         [data-search-input] + [data-search-results];
                                  fetches /search/suggest.json.
   <tc-modal id="...">            open with [data-modal-open="<id>"], close with
                                  [data-modal-close], ESC, backdrop.

   CART (window.ThrottleCart)
   - add(formData)        POST /cart/add.js, then refresh + open drawer
   - change(line, qty)    POST /cart/change.js, then refresh
   - refresh()            re-fetches current page HTML, swaps
                          [data-cart-drawer-content] and all [data-cart-count]
   Events: document fires 'cart:updated' (detail: cart JSON) after add/change.

   HELPERS: window.tcDebounce(fn, wait), window.tcFormatMoney(cents)
   ========================================================================== */

(function () {
  'use strict';

  /* ------------------------------------------------------------------------
     Helpers
     ------------------------------------------------------------------------ */

  /** Classic trailing-edge debounce. */
  function debounce(fn, wait) {
    let timer = null;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  }
  window.tcDebounce = debounce;

  /** Format cents using the shop money format from theme.liquid. */
  function formatMoney(cents) {
    const format = (window.themeSettings && window.themeSettings.moneyFormat) || '${{amount}}';
    const value = (cents / 100).toFixed(2);
    const [whole, decimals] = value.split('.');
    const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format
      .replace(/\{\{\s*amount\s*\}\}/, `${withCommas}.${decimals}`)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, `${whole}.${decimals}`.replace('.', ','));
  }
  window.tcFormatMoney = formatMoney;

  const strings = window.themeStrings || {};
  const routes = window.routes || {};

  /* ------------------------------------------------------------------------
     DetailsDisclosure — accessible <details> wrapper
     ------------------------------------------------------------------------ */
  class DetailsDisclosure extends HTMLElement {
    constructor() {
      super();
      this.details = this.querySelector('details');
      this.summary = this.details && this.details.querySelector('summary');
      if (!this.details || !this.summary) return;

      this.summary.setAttribute('aria-expanded', String(this.details.open));
      this.details.addEventListener('toggle', () => {
        this.summary.setAttribute('aria-expanded', String(this.details.open));
      });
      this.addEventListener('keyup', (event) => {
        if (event.key === 'Escape' && this.details.open) {
          this.close();
          this.summary.focus();
        }
      });
      this.addEventListener('focusout', () => {
        // Close when focus leaves the component entirely (keyboard users).
        requestAnimationFrame(() => {
          if (!this.contains(document.activeElement)) this.close();
        });
      });
    }

    open() {
      if (this.details) this.details.open = true;
    }

    close() {
      if (this.details) this.details.open = false;
    }
  }
  customElements.define('tc-details-disclosure', DetailsDisclosure);

  /* ------------------------------------------------------------------------
     MegaMenu — header dropdown with hover intent + outside click close
     ------------------------------------------------------------------------ */
  class MegaMenu extends DetailsDisclosure {
    constructor() {
      super();
      if (!this.details) return;
      this.hoverTimer = null;

      if (this.hasAttribute('data-hover') && window.matchMedia('(hover: hover)').matches) {
        this.addEventListener('mouseenter', () => {
          clearTimeout(this.hoverTimer);
          this.hoverTimer = setTimeout(() => this.open(), 80);
        });
        this.addEventListener('mouseleave', () => {
          clearTimeout(this.hoverTimer);
          this.hoverTimer = setTimeout(() => this.close(), 180);
        });
      }

      // Close when clicking anywhere outside the open menu.
      document.addEventListener('click', (event) => {
        if (this.details.open && !this.contains(event.target)) this.close();
      });
    }
  }
  customElements.define('tc-mega-menu', MegaMenu);

  /* ------------------------------------------------------------------------
     Drawer manager — mobile nav, cart drawer, filter drawer
     ------------------------------------------------------------------------ */
  const drawerManager = {
    activeId: null,
    lastTrigger: null,

    get(id) {
      return document.getElementById(id);
    },

    open(id, trigger) {
      const drawer = this.get(id);
      if (!drawer) return;
      // Only one drawer at a time.
      if (this.activeId && this.activeId !== id) this.close(this.activeId);

      drawer.classList.add('is-open');
      document.body.classList.add('tc-no-scroll');
      this.activeId = id;
      this.lastTrigger = trigger || null;
      this.syncTriggers(id, true);

      const panel = drawer.querySelector('.tc-drawer__panel');
      if (panel) panel.focus({ preventScroll: true });
    },

    close(id) {
      const drawer = this.get(id || this.activeId);
      if (!drawer) return;
      drawer.classList.remove('is-open');
      document.body.classList.remove('tc-no-scroll');
      this.syncTriggers(drawer.id, false);
      if (this.activeId === drawer.id) this.activeId = null;
      if (this.lastTrigger) {
        this.lastTrigger.focus({ preventScroll: true });
        this.lastTrigger = null;
      }
    },

    closeAll() {
      document.querySelectorAll('tc-drawer.is-open').forEach((d) => this.close(d.id));
    },

    syncTriggers(id, expanded) {
      document
        .querySelectorAll(`[data-drawer-open="${id}"]`)
        .forEach((btn) => btn.setAttribute('aria-expanded', String(expanded)));
    },
  };
  window.tcDrawers = drawerManager;

  class Drawer extends HTMLElement {}
  customElements.define('tc-drawer', Drawer);

  // Delegated open/close clicks (works for content swapped in via AJAX).
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      event.preventDefault();
      drawerManager.open(opener.getAttribute('data-drawer-open'), opener);
      return;
    }
    const closer = event.target.closest('[data-drawer-close]');
    if (closer) {
      const drawer = closer.closest('tc-drawer');
      if (drawer) {
        event.preventDefault();
        drawerManager.close(drawer.id);
      }
    }
  });

  /* ------------------------------------------------------------------------
     Modal utility
     ------------------------------------------------------------------------ */
  const modalManager = {
    activeId: null,
    lastTrigger: null,

    open(id, trigger) {
      const modal = document.getElementById(id);
      if (!modal) return;
      modal.classList.add('is-open');
      document.body.classList.add('tc-no-scroll');
      this.activeId = id;
      this.lastTrigger = trigger || null;
      const content = modal.querySelector('.tc-modal__content');
      if (content) {
        content.setAttribute('tabindex', '-1');
        content.focus({ preventScroll: true });
      }
    },

    close(id) {
      const modal = document.getElementById(id || this.activeId);
      if (!modal) return;
      modal.classList.remove('is-open');
      document.body.classList.remove('tc-no-scroll');
      if (this.activeId === modal.id) this.activeId = null;
      if (this.lastTrigger) {
        this.lastTrigger.focus({ preventScroll: true });
        this.lastTrigger = null;
      }
    },
  };
  window.tcModals = modalManager;

  class Modal extends HTMLElement {}
  customElements.define('tc-modal', Modal);

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-modal-open]');
    if (opener) {
      event.preventDefault();
      modalManager.open(opener.getAttribute('data-modal-open'), opener);
      return;
    }
    const closer = event.target.closest('[data-modal-close], .tc-modal__backdrop');
    if (closer) {
      const modal = closer.closest('tc-modal');
      if (modal) modalManager.close(modal.id);
    }
  });

  // ESC closes whatever overlay is open (drawer or modal).
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    if (modalManager.activeId) modalManager.close(modalManager.activeId);
    if (drawerManager.activeId) drawerManager.close(drawerManager.activeId);
  });

  /* ------------------------------------------------------------------------
     Sticky header
     ------------------------------------------------------------------------ */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      if (!this.hasAttribute('data-sticky')) return;
      this.onScroll = () => {
        this.classList.toggle('is-stuck', window.scrollY > 4);
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }

    disconnectedCallback() {
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    }
  }
  customElements.define('tc-sticky-header', StickyHeader);

  /* ------------------------------------------------------------------------
     Announcement bar rotation
     ------------------------------------------------------------------------ */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-announcement-slide]'));
      if (this.slides.length < 2) return;

      this.index = 0;
      this.interval = parseInt(this.getAttribute('data-rotate-interval'), 10) || 5000;
      this.start();

      // Pause on hover / when hidden; respect reduced motion.
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) this.stop();
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      document.addEventListener('visibilitychange', () => {
        document.hidden ? this.stop() : this.start();
      });
    }

    disconnectedCallback() {
      this.stop();
    }

    start() {
      if (this.timer || this.slides.length < 2) return;
      this.timer = setInterval(() => this.next(), this.interval);
    }

    stop() {
      clearInterval(this.timer);
      this.timer = null;
    }

    next() {
      this.slides[this.index].hidden = true;
      this.slides[this.index].classList.remove('is-active');
      this.index = (this.index + 1) % this.slides.length;
      this.slides[this.index].hidden = false;
      this.slides[this.index].classList.add('is-active');
    }
  }
  customElements.define('tc-announcement-bar', AnnouncementBar);

  /* ------------------------------------------------------------------------
     Carousel — scroll-snap track with prev/next buttons
     ------------------------------------------------------------------------ */
  class Carousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      this.prevBtn = this.querySelector('[data-carousel-prev]');
      this.nextBtn = this.querySelector('[data-carousel-next]');
      if (!this.track) return;

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.scrollByPage(-1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.scrollByPage(1));

      this.updateButtons = this.updateButtons.bind(this);
      this.track.addEventListener('scroll', debounce(this.updateButtons, 60), { passive: true });
      window.addEventListener('resize', debounce(this.updateButtons, 120));
      this.updateButtons();
    }

    scrollByPage(direction) {
      // Scroll by ~one visible page of slides.
      const amount = this.track.clientWidth * 0.9 * direction;
      this.track.scrollBy({ left: amount, behavior: 'smooth' });
    }

    updateButtons() {
      if (!this.prevBtn && !this.nextBtn) return;
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      const x = this.track.scrollLeft;
      if (this.prevBtn) this.prevBtn.toggleAttribute('disabled', x <= 2);
      if (this.nextBtn) this.nextBtn.toggleAttribute('disabled', x >= maxScroll - 2);
    }
  }
  customElements.define('tc-carousel', Carousel);

  /* ------------------------------------------------------------------------
     Quantity stepper
     ------------------------------------------------------------------------ */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="number"]');
      if (!this.input) return;

      const minus = this.querySelector('[data-qty-minus]');
      const plus = this.querySelector('[data-qty-plus]');
      if (minus) minus.addEventListener('click', () => this.step(-1));
      if (plus) plus.addEventListener('click', () => this.step(1));
    }

    step(delta) {
      const min = this.input.min !== '' ? parseInt(this.input.min, 10) : 0;
      const max = this.input.max !== '' ? parseInt(this.input.max, 10) : Infinity;
      const current = parseInt(this.input.value, 10) || 0;
      const next = Math.min(max, Math.max(min, current + delta));
      if (next === current) return;
      this.input.value = next;
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
  customElements.define('tc-quantity', QuantityInput);

  /* ------------------------------------------------------------------------
     AJAX cart
     ------------------------------------------------------------------------ */
  const ThrottleCart = {
    busy: false,

    setBusy(state) {
      this.busy = state;
      const drawer = document.querySelector('[data-cart-drawer]');
      if (drawer) drawer.classList.toggle('is-busy', state);
    },

    /** Add to cart. body = FormData from a product form. */
    async add(body) {
      this.setBusy(true);
      try {
        const response = await fetch(`${routes.cart_add_url || '/cart/add'}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body,
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || strings.cartError);
        await this.refresh();
        if ((window.themeSettings || {}).cartType === 'drawer') {
          drawerManager.open('tc-cart-drawer');
        }
        return data;
      } finally {
        this.setBusy(false);
      }
    },

    /** Change a line quantity (1-based line index). quantity 0 removes. */
    async change(line, quantity) {
      this.setBusy(true);
      try {
        const response = await fetch(`${routes.cart_change_url || '/cart/change'}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ line, quantity }),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || strings.cartError);
        await this.refresh(data);
        return data;
      } finally {
        this.setBusy(false);
      }
    },

    /**
     * Refresh cart UI. Re-fetches the current page and swaps:
     *  - [data-cart-drawer-content]  (drawer line items / totals)
     *  - all [data-cart-count]       (header bubbles)
     * The drawer is rendered in theme.liquid, so it exists on every page.
     */
    async refresh(cartData) {
      const cart =
        cartData ||
        (await fetch(`${routes.cart_url || '/cart'}.js`, {
          headers: { Accept: 'application/json' },
        }).then((r) => r.json()));

      // Update count bubbles immediately.
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.classList.toggle('is-empty', cart.item_count === 0);
      });

      // Re-render the drawer markup from the server.
      const target = document.querySelector('#tc-cart-drawer [data-cart-drawer-content]');
      if (target) {
        try {
          const html = await fetch(window.location.href, {
            headers: { Accept: 'text/html' },
          }).then((r) => r.text());
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.querySelector('#tc-cart-drawer [data-cart-drawer-content]');
          if (fresh) target.innerHTML = fresh.innerHTML;
        } catch (error) {
          // Non-fatal: counts are already updated.
          console.warn('[ThrottleCart] drawer refresh failed', error);
        }
      }

      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      return cart;
    },
  };
  window.ThrottleCart = ThrottleCart;

  /* Product form — AJAX add-to-cart (product cards + PDP buy box) */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
    }

    async onSubmit(event) {
      event.preventDefault();

      /* Backordered variant (0 stock, continue selling)? Ask first. */
      if (window.TcBackorder && window.TcBackorder.shouldPrompt(this)) {
        window.TcBackorder.prompt(this);
        return;
      }

      const button = this.form.querySelector('[name="add"], [type="submit"]');
      const label = button && button.querySelector('[data-atc-text]');

      if (button) {
        button.classList.add('is-loading');
        button.setAttribute('aria-disabled', 'true');
      }
      try {
        await ThrottleCart.add(new FormData(this.form));
        if (label) {
          const original = label.textContent;
          label.textContent = strings.added || 'Added';
          setTimeout(() => {
            label.textContent = original;
          }, 1800);
        }
      } catch (error) {
        window.alert(error.message || strings.cartError || 'Cart error');
      } finally {
        delete this.dataset.boConfirmed;
        if (button) {
          button.classList.remove('is-loading');
          button.removeAttribute('aria-disabled');
        }
      }
    }
  }
  customElements.define('tc-product-form', ProductForm);

  /* ------------------------------------------------------------------------
     Backorder confirmation (window.TcBackorder)
     Product forms carry data-backorder-variants='[ids]' for variants with
     0 inventory that are still sellable. Before adding one, the backorder
     modal explains the 2–5 week ship window; "Confirm & Add To Cart"
     resubmits the pending form with a one-shot confirmation flag.
     ------------------------------------------------------------------------ */
  window.TcBackorder = {
    pending: null,

    shouldPrompt(formEl) {
      if (formEl.dataset.boConfirmed === '1') return false;
      if (!document.getElementById('tc-backorder-modal')) return false;
      const raw = formEl.getAttribute('data-backorder-variants');
      if (!raw) return false;
      let ids;
      try {
        ids = JSON.parse(raw);
      } catch (e) {
        return false;
      }
      const form = formEl.querySelector('form');
      if (!form) return false;
      const variantId = parseInt(new FormData(form).get('id'), 10);
      return Array.isArray(ids) && ids.indexOf(variantId) !== -1;
    },

    prompt(formEl) {
      this.pending = formEl;
      window.tcModals.open('tc-backorder-modal');
    }
  };

  document.addEventListener('click', (event) => {
    const confirmBtn = event.target.closest('[data-backorder-confirm]');
    if (!confirmBtn) return;
    const pending = window.TcBackorder.pending;
    window.tcModals.close('tc-backorder-modal');
    if (!pending) return;
    window.TcBackorder.pending = null;
    pending.dataset.boConfirmed = '1';
    const form = pending.querySelector('form');
    if (form) {
      if (typeof form.requestSubmit === 'function') form.requestSubmit();
      else form.dispatchEvent(new Event('submit', { cancelable: true }));
    }
  });

  /* Cart drawer line-item interactions (delegated — survives innerHTML swaps) */
  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-cart-qty-input]');
    if (!input) return;
    const line = parseInt(input.getAttribute('data-line'), 10);
    const quantity = Math.max(0, parseInt(input.value, 10) || 0);
    if (line) ThrottleCart.change(line, quantity).catch(() => window.alert(strings.cartError));
  });

  document.addEventListener('click', (event) => {
    const removeBtn = event.target.closest('[data-cart-remove]');
    if (!removeBtn) return;
    event.preventDefault();
    const line = parseInt(removeBtn.getAttribute('data-line'), 10);
    if (line) ThrottleCart.change(line, 0).catch(() => window.alert(strings.cartError));
  });

  /* ------------------------------------------------------------------------
     Predictive search
     ------------------------------------------------------------------------ */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-search-input]');
      this.results = this.querySelector('[data-search-results]');
      if (!this.input || !this.results || !this.hasAttribute('data-predictive')) return;

      this.abortController = null;
      this.input.addEventListener(
        'input',
        debounce(() => this.onInput(), 250)
      );
      this.input.addEventListener('focus', () => {
        if (this.results.childElementCount > 0 && this.input.value.trim().length > 1) {
          this.show();
        }
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.hide();
      });
      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.hide();
      });
    }

    async onInput() {
      const query = this.input.value.trim();
      if (query.length < 2) {
        this.hide();
        return;
      }

      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();

      const url =
        `${routes.predictive_search_url || '/search/suggest'}.json` +
        `?q=${encodeURIComponent(query)}` +
        '&resources[type]=product' +
        '&resources[limit]=6' +
        '&resources[options][unavailable_products]=last';

      try {
        const response = await fetch(url, { signal: this.abortController.signal });
        if (!response.ok) return;
        const data = await response.json();
        this.render(data.resources.results.products || [], query);
      } catch (error) {
        if (error.name !== 'AbortError') console.warn('[PredictiveSearch]', error);
      }
    }

    render(products, query) {
      this.results.innerHTML = '';

      if (products.length === 0) {
        const empty = document.createElement('p');
        empty.className = 'tc-search__empty';
        empty.textContent = (strings.searchNoResults || 'No results for "[terms]"').replace(
          '[terms]',
          query
        );
        this.results.appendChild(empty);
        this.show();
        return;
      }

      const title = document.createElement('p');
      title.className = 'tc-search__group-title';
      title.textContent = strings.searchProducts || 'Products';
      this.results.appendChild(title);

      products.forEach((product) => {
        const link = document.createElement('a');
        link.className = 'tc-search__result';
        link.href = product.url;
        link.setAttribute('role', 'option');

        if (product.featured_image && product.featured_image.url) {
          const img = document.createElement('img');
          img.className = 'tc-search__result-img';
          img.src = product.featured_image.url;
          img.alt = '';
          img.width = 44;
          img.height = 44;
          img.loading = 'lazy';
          link.appendChild(img);
        }

        const info = document.createElement('span');
        const titleEl = document.createElement('span');
        titleEl.className = 'tc-search__result-title';
        titleEl.textContent = product.title;
        const priceEl = document.createElement('span');
        priceEl.className = 'tc-search__result-price';
        priceEl.textContent = ` ${formatMoney(parseFloat(product.price) * 100)}`;
        info.appendChild(titleEl);
        info.appendChild(document.createElement('br'));
        info.appendChild(priceEl);
        link.appendChild(info);

        this.results.appendChild(link);
      });

      const viewAll = document.createElement('a');
      viewAll.className = 'tc-search__view-all';
      viewAll.href = `${routes.search_url || '/search'}?q=${encodeURIComponent(query)}&options%5Bprefix%5D=last`;
      viewAll.textContent = strings.searchViewAll || 'View all results';
      this.results.appendChild(viewAll);

      this.show();
    }

    show() {
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    hide() {
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
    }
  }
  customElements.define('tc-predictive-search', PredictiveSearch);
})();

/* ==========================================================================
   CART INCENTIVES (UpCart replacement)
   - Gift tiers: reads [data-gift-tiers] JSON from the cart drawer and
     auto-adds/removes $0 gift variants as the cart crosses tier amounts.
     Gift lines are tagged with the line item property `_tc_gift`.
   - Upsells: fills [data-cart-upsells] with product recommendations based
     on the first non-gift cart item. One-tap add.
   ========================================================================== */
(function () {
  let reconciling = false;

  function getTierConfig() {
    const el = document.querySelector('#tc-cart-drawer [data-gift-tiers]');
    if (!el) return [];
    try {
      return JSON.parse(el.getAttribute('data-gift-tiers')) || [];
    } catch (e) {
      return [];
    }
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return res.json();
  }

  /* A line is a managed gift if we added it (tagged _tc_gift), or if it is a
     $0-priced tier product added by hand. A PAID line for the same product
     (e.g. a customer buying an extra phone bag) is NOT a gift — it counts
     toward the qualifying total and keeps its own quantity controls. */
  function isGiftItem(item, tiers) {
    if (item.properties && item.properties._tc_gift) return true;
    if (item.price !== 0) return false;
    return tiers.some(function (tier) { return tier.variant === item.variant_id; });
  }

  function isProtectionItem(item) {
    return !!(item.properties && item.properties._tc_protection);
  }

  /* Cart total excluding gift + protection lines. */
  function qualifyingTotal(cart, tiers) {
    return cart.items.reduce(function (sum, item) {
      if (isGiftItem(item, tiers) || isProtectionItem(item)) return sum;
      return sum + item.final_line_price;
    }, 0);
  }

  async function cartChange(body) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    return res.ok;
  }

  async function reconcileGifts() {
    const tiers = getTierConfig();
    if (!tiers.length || reconciling) return;
    reconciling = true;
    try {
      let cart = await fetchCart();
      const total = qualifyingTotal(cart, tiers);
      let changed = false;

      for (const tier of tiers) {
        cart = changed ? await fetchCart() : cart;
        const line = cart.items.findIndex(function (item) {
          return item.variant_id === tier.variant && isGiftItem(item, tiers);
        });
        const eligible = total >= tier.amount;

        if (eligible && line === -1) {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: tier.variant, quantity: 1, properties: { _tc_gift: tier.label || '1' } })
          });
          if (res.ok) changed = true;
        } else if (eligible && line !== -1 && cart.items[line].quantity !== 1) {
          /* One per order — clamp quantity back to 1. */
          if (await cartChange({ line: line + 1, quantity: 1 })) changed = true;
        } else if (!eligible && line !== -1) {
          if (await cartChange({ line: line + 1, quantity: 0 })) changed = true;
        }
      }

      if (changed && window.ThrottleCart) {
        /* refresh() fires cart:updated again; `reconciling` stays true until
           after the await below, and the re-entry guard skips the loop. */
        await window.ThrottleCart.refresh();
      }
    } catch (e) {
      console.warn('[TcIncentives] gift reconcile failed', e);
    } finally {
      reconciling = false;
    }
  }

  /* ---------------- Upsells ---------------- */
  function money(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  async function renderUpsells() {
    const wrap = document.querySelector('#tc-cart-drawer [data-cart-upsells]');
    const track = wrap && wrap.querySelector('[data-cart-upsells-track]');
    if (!wrap || !track) return;

    try {
      const cart = await fetchCart();
      const tiers = getTierConfig();
      const seed = cart.items.find(function (item) {
        return !isGiftItem(item, tiers) && !isProtectionItem(item);
      });
      if (!seed) {
        wrap.hidden = true;
        return;
      }

      const inCart = cart.items.map(function (item) { return item.product_id; });
      const res = await fetch(
        window.Shopify && window.Shopify.routes && window.Shopify.routes.root
          ? window.Shopify.routes.root + 'recommendations/products.json?product_id=' + seed.product_id + '&limit=8&intent=related'
          : '/recommendations/products.json?product_id=' + seed.product_id + '&limit=8&intent=related'
      );
      if (!res.ok) { wrap.hidden = true; return; }
      const data = await res.json();
      const picks = (data.products || [])
        .filter(function (p) { return inCart.indexOf(p.id) === -1 && p.available && p.price > 0; })
        .slice(0, 4);

      if (!picks.length) {
        wrap.hidden = true;
        return;
      }

      track.innerHTML = picks.map(function (p) {
        const variant = (p.variants || []).filter(function (v) { return v.available; })[0] || p.variants[0];
        if (!variant) return '';
        const img = p.featured_image ? p.featured_image + '&width=160' : '';
        return (
          '<div class="tc-upsell-card">' +
            (img ? '<img src="' + img + '" alt="" loading="lazy" width="72" height="72">' : '') +
            '<div class="tc-upsell-card__info">' +
              '<a href="' + p.url + '" class="tc-upsell-card__title">' + p.title + '</a>' +
              '<span class="tc-upsell-card__price">' + money(variant.price) + '</span>' +
            '</div>' +
            '<button type="button" class="tc-btn tc-btn--secondary tc-btn--small" data-upsell-add="' + variant.id + '">+</button>' +
          '</div>'
        );
      }).join('');
      wrap.hidden = false;
    } catch (e) {
      console.warn('[TcIncentives] upsells failed', e);
      if (wrap) wrap.hidden = true;
    }
  }

  document.addEventListener('click', function (event) {
    const btn = event.target.closest('[data-upsell-add]');
    if (!btn) return;
    btn.disabled = true;
    const fd = new FormData();
    fd.append('id', btn.getAttribute('data-upsell-add'));
    fd.append('quantity', '1');
    if (window.ThrottleCart) {
      window.ThrottleCart.add(fd).catch(function () { btn.disabled = false; });
    }
  });

  function runAll() {
    reconcileGifts().then(renderUpsells);
  }

  document.addEventListener('cart:updated', runAll);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runAll);
  } else {
    runAll();
  }
})();

/* ==========================================================================
   CART RESERVATION COUNTDOWN
   Urgency banner in the cart drawer: "Your products are reserved for MM:SS!"
   The end time lives in sessionStorage so it survives drawer re-renders and
   page navigation within the visit. When it hits 0 it quietly restarts.
   ========================================================================== */
(function () {
  const KEY = 'tc_cart_timer_end';

  function format(totalSeconds) {
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return (m < 10 ? '0' + m : m) + ':' + (s < 10 ? '0' + s : s);
  }

  function tick() {
    const banner = document.querySelector('[data-cart-timer]');
    if (!banner) return;
    const minutes = parseInt(banner.getAttribute('data-timer-minutes'), 10) || 10;
    const now = Date.now();
    let end = parseInt(sessionStorage.getItem(KEY), 10);

    if (!end || isNaN(end) || end <= now) {
      end = now + minutes * 60 * 1000;
      try { sessionStorage.setItem(KEY, String(end)); } catch (e) { /* private mode */ }
    }

    const remaining = Math.max(0, Math.round((end - now) / 1000));
    document.querySelectorAll('[data-cart-timer-time]').forEach(function (el) {
      el.textContent = format(remaining);
    });
  }

  setInterval(tick, 1000);
  document.addEventListener('cart:updated', tick);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tick);
  } else {
    tick();
  }
})();

/* ==========================================================================
   SHIPPING PROTECTION TOGGLE (5% of order value)
   The protection product has one variant per order-value band, priced at 5%
   of the band cap. The toggle adds the right band for the current cart, and
   the reconciler swaps bands automatically as the cart total changes.
   Protection lines carry the line item property `_tc_protection`.
   ========================================================================== */
(function () {
  let reconciling = false;

  function getConfig() {
    const toggle = document.querySelector('#tc-cart-drawer [data-protection-toggle]');
    if (!toggle) return null;
    try {
      const variants = JSON.parse(toggle.getAttribute('data-protection-variants')) || [];
      if (!variants.length) return null;
      return { variants: variants };
    } catch (e) {
      return null;
    }
  }

  function isProtection(item, config) {
    if (item.properties && item.properties._tc_protection) return true;
    return config.variants.some(function (v) { return v.id === item.variant_id; });
  }

  function pickVariant(basisCents, variants) {
    const match = variants.find(function (v) { return v.max > 0 && basisCents <= v.max; });
    return match || variants[variants.length - 1];
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return res.json();
  }

  async function cartPost(path, body) {
    const res = await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    return res.ok;
  }

  async function removeProtectionLines(cart, config) {
    let removed = false;
    /* Remove from the end so line numbers stay valid. */
    for (let i = cart.items.length - 1; i >= 0; i--) {
      if (isProtection(cart.items[i], config)) {
        if (await cartPost('/cart/change.js', { line: i + 1, quantity: 0 })) removed = true;
      }
    }
    return removed;
  }

  function basisOf(cart, config) {
    return cart.items.reduce(function (sum, item) {
      if (isProtection(item, config)) return sum;
      return sum + item.final_line_price;
    }, 0);
  }

  async function addProtection(config, cart) {
    const variant = pickVariant(basisOf(cart, config), config.variants);
    return cartPost('/cart/add.js', {
      id: variant.id,
      quantity: 1,
      properties: { _tc_protection: '1' }
    });
  }

  /* Keep the protection band in sync with the cart total. */
  async function reconcileProtection() {
    const config = getConfig();
    if (!config || reconciling) return;
    reconciling = true;
    try {
      const cart = await fetchCart();
      const lines = cart.items.filter(function (item) { return isProtection(item, config); });
      if (!lines.length) return;

      const correct = pickVariant(basisOf(cart, config), config.variants);
      const wrong = lines.length > 1
        || lines[0].variant_id !== correct.id
        || lines[0].quantity !== 1;

      if (wrong) {
        await removeProtectionLines(cart, config);
        await cartPost('/cart/add.js', { id: correct.id, quantity: 1, properties: { _tc_protection: '1' } });
        if (window.ThrottleCart) await window.ThrottleCart.refresh();
      }
    } catch (e) {
      console.warn('[TcProtection] reconcile failed', e);
    } finally {
      reconciling = false;
    }
  }

  document.addEventListener('change', async function (event) {
    const toggle = event.target.closest('[data-protection-toggle]');
    if (!toggle) return;
    const config = getConfig();
    if (!config) return;
    toggle.disabled = true;

    try {
      const cart = await fetchCart();
      if (toggle.checked) {
        await addProtection(config, cart);
      } else {
        await removeProtectionLines(cart, config);
      }
      if (window.ThrottleCart) await window.ThrottleCart.refresh();
    } catch (e) {
      console.warn('[TcProtection] toggle failed', e);
      toggle.disabled = false;
      toggle.checked = !toggle.checked;
    }
  });

  document.addEventListener('cart:updated', reconcileProtection);
})();

/* ==========================================================================
   CART DISCOUNT CODE
   Applies a code via the /discount/CODE endpoint (sets the discount cookie),
   then refreshes the drawer. If the cart-level discount is visible right
   away we confirm it; otherwise we let the shopper know it will apply at
   checkout if valid.
   ========================================================================== */
(function () {
  async function getCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return res.json();
  }

  async function applyDiscount(wrap) {
    const input = wrap.querySelector('[data-discount-input]');
    const btn = wrap.querySelector('[data-discount-apply]');
    const code = input && input.value.trim();
    if (!code) return;
    if (btn) btn.disabled = true;

    const msgApplied = wrap.getAttribute('data-msg-applied') || 'Discount applied!';
    const msgSaved = wrap.getAttribute('data-msg-saved') || 'Code saved — it will apply at checkout if valid.';

    try {
      const before = await getCart();
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      await fetch(root + 'discount/' + encodeURIComponent(code), { redirect: 'follow' });
      if (window.ThrottleCart) await window.ThrottleCart.refresh();
      const after = await getCart();

      /* The drawer content was just re-rendered — find the fresh message node. */
      const msg = document.querySelector('#tc-cart-drawer [data-discount-msg]');
      if (msg) {
        const discountGrew = (after.total_discount || 0) > (before.total_discount || 0)
          || after.total_price < before.total_price;
        msg.textContent = discountGrew ? msgApplied : msgSaved;
        msg.classList.toggle('is-success', discountGrew);
        msg.hidden = false;
      }
    } catch (e) {
      console.warn('[TcDiscount] apply failed', e);
      if (btn) btn.disabled = false;
    }
  }

  document.addEventListener('click', function (event) {
    const btn = event.target.closest('[data-discount-apply]');
    if (!btn) return;
    const wrap = btn.closest('[data-cart-discount]');
    if (wrap) applyDiscount(wrap);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter') return;
    const input = event.target.closest('[data-discount-input]');
    if (!input) return;
    event.preventDefault();
    const wrap = input.closest('[data-cart-discount]');
    if (wrap) applyDiscount(wrap);
  });
})();

/* ==========================================================================
   BIKE GARAGE (window.TcGarage)
   The shopper picks their bike once (tc-garage-modal); it's stored in
   localStorage and drives:
   - the header button label ([data-garage-label] + [data-garage-kicker])
   - the first-visit bar under the header ([data-garage-bar])
   - "Fits your bike" badges on product cards ([data-fitment] + [data-fit-slot])
   - the fitment banner on product pages ([data-fitment-banner])
   - the fitment summary on collection and search grids: every card with a
     data-fitment list gets a "Fits your bike" or "Not listed" badge, cards
     without a list are not bike-specific (merch, gear) and get nothing, and
     nothing is hidden unless the rider taps "Hide the N not listed" (Bryce:
     fitment does not apply to a lot of the catalog, so never hide by default)
   "I don't have a bike yet" stores {none:true}: no badges, no summary, no nag.
   Strings come from data-t-* attributes on #tc-garage-modal; "[bike]",
   "[count]" and "[fits]" are replaced.
   ========================================================================== */
(function () {
  const KEY = 'tc_garage';
  const BAR_KEY = 'tc_garage_bar_dismissed';
  const HIDE_KEY = 'tc_garage_hide';

  function lsGet(key) { try { return localStorage.getItem(key); } catch (e) { return null; } }
  function lsSet(key, value) { try { if (value === null) localStorage.removeItem(key); else localStorage.setItem(key, value); } catch (e) { /* private mode */ } }

  function get() {
    try {
      return JSON.parse(localStorage.getItem(KEY));
    } catch (e) {
      return null;
    }
  }
  function hasBike(g) { return !!g && !g.none && !!g.label; }

  function set(bike) {
    lsSet(KEY, bike ? JSON.stringify(bike) : null);
    document.dispatchEvent(new CustomEvent('garage:changed'));
  }

  function t(name, bike, count, fits) {
    const modal = document.getElementById('tc-garage-modal');
    const raw = (modal && modal.getAttribute('data-t-' + name)) || '';
    return raw.replace('[bike]', bike || '').replace('[count]', count === undefined ? '' : String(count)).replace('[fits]', fits === undefined ? '' : String(fits));
  }

  function closeModal() {
    if (window.tcModals && typeof window.tcModals.close === 'function') window.tcModals.close('tc-garage-modal');
  }

  function refreshBar(g) {
    const bar = document.querySelector('[data-garage-bar]');
    if (!bar) return;
    bar.hidden = !!g || lsGet(BAR_KEY) === '1';
  }

  function refreshFilter(g) {
    const body = document.body;
    const onGrid = !!body && (body.classList.contains('template-collection') || body.classList.contains('template-search'));
    const grids = onGrid ? Array.prototype.slice.call(document.querySelectorAll('.tc-collection-main .tc-grid, .tc-search-results .tc-grid, [data-garage-filter-grid]')) : [];
    grids.forEach(function (grid) {
      let note = grid.previousElementSibling && grid.previousElementSibling.matches('[data-garage-note]') ? grid.previousElementSibling : null;
      const cards = Array.prototype.slice.call(grid.querySelectorAll('[data-product-card]'));
      if (!hasBike(g)) {
        cards.forEach(function (c) { (c.closest('li') || c).classList.remove('is-garage-hidden'); });
        if (note) note.remove();
        return;
      }
      const hide = lsGet(HIDE_KEY) === '1';
      let misses = 0;
      let fits = 0;
      cards.forEach(function (c) {
        const raw = c.getAttribute('data-fitment');
        const host = c.closest('li') || c;
        if (!raw) { host.classList.remove('is-garage-hidden'); return; }
        const fit = raw.split('|').indexOf(g.label) !== -1;
        if (fit) fits++; else misses++;
        host.classList.toggle('is-garage-hidden', !fit && hide);
      });
      if (!note) {
        note = document.createElement('div');
        note.className = 'tc-garage-note';
        note.setAttribute('data-garage-note', '');
        grid.parentNode.insertBefore(note, grid);
      }
      const bike = '<b>' + g.label.replace(/</g, '&lt;') + '</b>';
      if (misses === 0) {
        note.className = 'tc-garage-note';
        note.innerHTML = '<span>' + t('filter-all-fit', bike) + '</span>';
      } else if (hide) {
        note.className = 'tc-garage-note tc-garage-note--muted';
        note.innerHTML = '<span>' + t('filter-hiding', bike, misses) + '</span> <button type="button" class="tc-garage-note__btn" data-garage-hide="0">' + t('filter-show-all') + '</button>';
      } else {
        note.className = 'tc-garage-note';
        note.innerHTML = '<span>' + t('filter-summary', bike, misses, fits) + '</span> <button type="button" class="tc-garage-note__btn" data-garage-hide="1">' + t('filter-hide', bike, misses) + '</button>';
      }
    });
  }

  function refresh() {
    const g = get();
    const picked = hasBike(g);

    document.querySelectorAll('[data-garage-label]').forEach(function (el) {
      el.textContent = picked ? g.label : (g && g.none ? el.getAttribute('data-none') : el.getAttribute('data-default')) || el.textContent;
    });
    document.querySelectorAll('[data-garage-kicker]').forEach(function (el) {
      el.textContent = (picked ? el.getAttribute('data-picked') : el.getAttribute('data-default')) || el.textContent;
    });
    document.querySelectorAll('[data-garage-btn]').forEach(function (el) {
      el.classList.toggle('is-picked', picked);
    });

    document.querySelectorAll('[data-garage-pick]').forEach(function (btn) {
      btn.classList.toggle('is-active', picked && btn.getAttribute('data-label') === g.label);
    });
    document.querySelectorAll('[data-garage-none]').forEach(function (btn) {
      btn.classList.toggle('is-active', !!g && !!g.none);
    });

    const shop = document.querySelector('[data-garage-shop]');
    if (shop) {
      shop.hidden = !picked;
      if (picked) {
        shop.setAttribute('href', (window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/') + 'collections/' + g.handle);
        const label = shop.querySelector('[data-garage-shop-label]');
        if (label) label.textContent = g.label;
      }
    }
    const clear = document.querySelector('[data-garage-clear]');
    if (clear) clear.hidden = !g;

    document.querySelectorAll('[data-fitment]').forEach(function (el) {
      const list = (el.getAttribute('data-fitment') || '').split('|');
      const fits = picked && list.indexOf(g.label) !== -1;

      const slot = el.querySelector('[data-fit-slot]');
      if (slot) {
        // A card with a fitment list always says something once a bike is picked; a card without one stays quiet.
        const listed = list.filter(Boolean).length > 0;
        slot.hidden = !(picked && listed);
        slot.innerHTML = !picked || !listed ? '' : fits
          ? '<span class="tc-fit-badge">&#10003; ' + t('fits', g.label) + '</span>'
          : '<span class="tc-fit-badge tc-fit-badge--warn">&#9888; ' + t('not-listed', g.label) + '</span>';
      }

      const banner = el.querySelector('[data-fitment-banner]');
      if (banner) {
        if (g && g.none) {
          banner.hidden = true;
        } else if (!g) {
          banner.hidden = false;
          banner.className = 'tc-fitment__banner tc-fitment__banner--pick';
          banner.innerHTML = '<button type="button" class="tc-fitment__pick-btn" data-modal-open="tc-garage-modal">' + t('pick') + '</button>';
        } else if (fits) {
          banner.hidden = false;
          banner.className = 'tc-fitment__banner tc-fitment__banner--fits';
          banner.innerHTML = '&#10003; ' + t('fits', g.label);
        } else {
          banner.hidden = false;
          banner.className = 'tc-fitment__banner tc-fitment__banner--warn';
          banner.innerHTML = '&#9888; ' + t('check', g.label);
        }
      }
    });

    refreshBar(g);
    refreshFilter(g);
  }

  document.addEventListener('click', function (event) {
    const pick = event.target.closest('[data-garage-pick]');
    if (pick) {
      set({ label: pick.getAttribute('data-label'), handle: pick.getAttribute('data-handle') });
      closeModal();
      return;
    }
    if (event.target.closest('[data-garage-none]')) { set({ none: true }); closeModal(); return; }
    if (event.target.closest('[data-garage-clear]')) { set(null); return; }
    if (event.target.closest('[data-garage-bar-close]')) { lsSet(BAR_KEY, '1'); refreshBar(get()); return; }
    const toggle = event.target.closest('[data-garage-hide]');
    if (toggle) { lsSet(HIDE_KEY, toggle.getAttribute('data-garage-hide') === '1' ? '1' : null); refreshFilter(get()); }
  });

  document.addEventListener('garage:changed', refresh);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh);
  } else {
    refresh();
  }

  window.TcGarage = { get: get, set: set, refresh: refresh, hasBike: function () { return hasBike(get()); } };
})();
